// Firebase Services
// Handles authentication and Firestore operations

import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged, signOut, signInWithPopup, GoogleAuthProvider, OAuthProvider, linkWithCredential, unlink, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { getFirestore, collection, doc, addDoc, updateDoc, deleteDoc, getDoc, getDocs, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { firebaseConfig } from './firebase-config.js';

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Authentication services
export const authService = {
  // Sign in with Google
  signInWithGoogle: async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      return { user: result.user, error: null };
    } catch (error) {
      console.error('Google sign-in error:', error);
      return { user: null, error: error.message };
    }
  },

  // Sign in with Apple
  signInWithApple: async () => {
    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      const result = await signInWithPopup(auth, provider);
      return { user: result.user, error: null };
    } catch (error) {
      console.error('Apple sign-in error:', error);
      return { user: null, error: error.message };
    }
  },

  // Sign in with Microsoft
  signInWithMicrosoft: async () => {
    try {
      const provider = new OAuthProvider('microsoft.com');
      provider.addScope('email');
      provider.addScope('profile');
      const result = await signInWithPopup(auth, provider);
      return { user: result.user, error: null };
    } catch (error) {
      console.error('Microsoft sign-in error:', error);
      return { user: null, error: error.message };
    }
  },

  // Sign in with email and password
  signInWithEmail: async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return { user: result.user, error: null };
    } catch (error) {
      console.error('Email sign-in error:', error);
      return { user: null, error: error.message };
    }
  },

  // Create user with email and password
  createEmailUser: async (email, password) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Send email verification - use the current user from auth
      if (result.user) {
        // Small delay to ensure user is fully initialized
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Use auth.currentUser instead of result.user to ensure we have the right user object
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.email === email) {
          
          const actionCodeSettings = {
            url: window.location.origin,
            handleCodeInApp: true
          };
          
          try {
            await sendEmailVerification(currentUser, actionCodeSettings);
            console.log('Email verification sent successfully');
          } catch (error) {
            console.error('Failed to send email verification:', error);
            console.warn('Email verification failed but user creation succeeded');
          }
        } else {
          console.warn('Current user does not match expected email:', currentUser?.email);
        }
      }
      
      return { user: result.user, error: null, needsVerification: true };
    } catch (error) {
      console.error('Email user creation error:', error);
      return { user: null, error: error.message };
    }
  },

  // Send email verification
  sendEmailVerification: async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        // Create action code for email verification
        const actionCodeSettings = {
          url: window.location.origin,
          handleCodeInApp: true
        };
        
        await sendEmailVerification(user, actionCodeSettings);
        return { error: null };
      }
      return { error: 'No user logged in' };
    } catch (error) {
      console.error('Email verification error:', error);
      return { error: error.message };
    }
  },

  // Check if email is verified
  isEmailVerified: async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        await user.reload();
        return user.emailVerified;
      }
      return false;
    } catch (error) {
      console.error('Email verification check error:', error);
      return false;
    }
  },

  // Sign in anonymously for kindergarten users
  signInAnonymously: async () => {
    try {
      const result = await signInAnonymously(auth);
      return { user: result.user, error: null };
    } catch (error) {
      console.error('Anonymous sign-in error:', error);
      return { user: null, error: error.message };
    }
  },

  // Listen to auth state changes
  onAuthStateChanged: (callback) => {
    return onAuthStateChanged(auth, callback);
  },

  // Sign out user
  signOut: async () => {
    try {
      await signOut(auth);
      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      return { error: error.message };
    }
  },

  // Get current user
  getCurrentUser: () => auth.currentUser,

  // Link anonymous account with provider
  linkAnonymousAccount: async (provider) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser || currentUser.isAnonymous) {
        const result = await signInWithPopup(auth, provider);
        return { user: result.user, error: null };
      }
      return { user: currentUser, error: null };
    } catch (error) {
      console.error('Account linking error:', error);
      return { user: null, error: error.message };
    }
  }
};

// Firestore services for flashcard lists
export const flashcardService = {
  // Get reference to user's flashcard lists collection
  getUserListsRef: (userId) => {
    return collection(db, 'artifacts', 'kindergarten-flashcards', 'users', userId, 'flashcard_lists');
  },

  // Get reference to a specific list
  getListRef: (userId, listId) => {
    return doc(db, 'artifacts', 'kindergarten-flashcards', 'users', userId, 'flashcard_lists', listId);
  },

  // Fetch all flashcard lists for a user
  fetchLists: async (userId) => {
    try {
      const q = query(
        flashcardService.getUserListsRef(userId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const lists = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return { lists, error: null };
    } catch (error) {
      console.error('Error fetching lists:', error);
      return { lists: [], error: error.message };
    }
  },

  // Listen to real-time updates for user's lists
  onListsUpdate: (userId, callback) => {
    const q = query(
      flashcardService.getUserListsRef(userId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      const lists = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(lists);
    }, (error) => {
      console.error('Error listening to lists:', error);
      callback([]);
    });
  },

  // Create a new flashcard list
  createList: async (userId, listData) => {
    try {
      const docRef = await addDoc(flashcardService.getUserListsRef(userId), {
        ...listData,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return { id: docRef.id, error: null };
    } catch (error) {
      console.error('Error creating list:', error);
      return { id: null, error: error.message };
    }
  },

  // Update an existing flashcard list
  updateList: async (userId, listId, updateData) => {
    try {
      await updateDoc(flashcardService.getListRef(userId, listId), {
        ...updateData,
        updatedAt: new Date()
      });
      return { error: null };
    } catch (error) {
      console.error('Error updating list:', error);
      return { error: error.message };
    }
  },

  // Delete a flashcard list
  deleteList: async (userId, listId) => {
    try {
      await deleteDoc(flashcardService.getListRef(userId, listId));
      return { error: null };
    } catch (error) {
      console.error('Error deleting list:', error);
      return { error: error.message };
    }
  },

  // Get a specific flashcard list
  getList: async (userId, listId) => {
    try {
      const docSnap = await getDoc(flashcardService.getListRef(userId, listId));
      if (docSnap.exists()) {
        return { list: { id: docSnap.id, ...docSnap.data() }, error: null };
      } else {
        return { list: null, error: 'List not found' };
      }
    } catch (error) {
      console.error('Error getting list:', error);
      return { list: null, error: error.message };
    }
  }
};

// User profile services
export const userProfileService = {
  // Get reference to user's profile document
  getUserProfileRef: (userId) => {
    return doc(db, 'artifacts', 'kindergarten-flashcards', 'users', userId, 'profile');
  },

  // Create or update user profile
  saveUserProfile: async (userId, profileData) => {
    try {
      const profileRef = userProfileService.getUserProfileRef(userId);
      await updateDoc(profileRef, {
        ...profileData,
        updatedAt: new Date()
      });
      return { error: null };
    } catch (error) {
      console.error('Error saving user profile:', error);
      return { error: error.message };
    }
  },

  // Get user profile
  getUserProfile: async (userId) => {
    try {
      const docSnap = await getDoc(userProfileService.getUserProfileRef(userId));
      if (docSnap.exists()) {
        return { profile: docSnap.data(), error: null };
      } else {
        return { profile: null, error: 'Profile not found' };
      }
    } catch (error) {
      console.error('Error getting user profile:', error);
      return { profile: null, error: error.message };
    }
  }
};

// Export initialized Firebase instances
export { auth, db };
export default app;
